import { produce } from "immer";
import { GlobalActions, resetAction } from "../../globals/store/actions";
import { createEntityAdapter } from "../../helpers/store/create-entity-adapter";
import {
  loadDismissedNotificationIds,
  saveDismissedNotificationIds,
} from "../constants/dismissedNotificationStorage";
import { Notification } from "../interfaces/api/get-notifications";
import {
  clearNotifications,
  getNotificationsFailureAction,
  getNotificationsInitAction,
  getNotificationsSuccessAction,
  getSystemNotificationsFailureAction,
  getSystemNotificationsInitAction,
  getSystemNotificationsSuccessAction,
  getUnreadNotificationsSuccessAction,
  NotificationApiAction,
  putReadNotificationFailureAction,
  putReadNotificationInitAction,
  putReadNotificationsFailureAction,
  putReadNotificationsInitAction,
  putReadNotificationsSuccessAction,
  putReadNotificationSuccessAction,
  deleteNotificationSuccessAction,
  removeNotificationLocallySuccessAction,
  hydrateDismissedNotificationsAction,
} from "./api-actions";

export interface NotificationState {
  /** Ids ocultados en cliente (API de borrado no disponible o error). Por empresa. */
  dismissedIdsByCompany: Record<string, string[]>;
  count: number;
  unreadCount: number;
  infoMessage: string | null;
  infoMessageEs: string | null;
  infoMessageEn: string | null;
  infoMessagePt: string | null;
  notifications: {
    ids: string[];
    entities: Record<string, Notification>;
  };
  unreadNotifications: {
    ids: string[];
    entities: Record<string, Notification>;
  };
}

const initialState: NotificationState = {
  dismissedIdsByCompany: {},
  count: 0,
  unreadCount: 0,
  infoMessage: null,
  infoMessageEs: null,
  infoMessageEn: null,
  infoMessagePt: null,
  notifications: {
    ids: [],
    entities: {},
  },
  unreadNotifications: {
    ids: [],
    entities: {},
  },
};

const notificationsAdapter = createEntityAdapter<Notification>(
  (payload) => payload.id
);
const unreadNotificationsAdapter = createEntityAdapter<Notification>(
  (payload) => payload.id
);

export default produce(
  (
    draft: NotificationState = initialState,
    action: NotificationApiAction | GlobalActions
  ) => {
    /** Estado persistido antiguo puede no traer este campo. */
    if (!draft.dismissedIdsByCompany) {
      draft.dismissedIdsByCompany = {};
    }
    switch (action.type) {
      case clearNotifications.type:
        notificationsAdapter.removeAll(draft.notifications);

        return draft;
      case getNotificationsSuccessAction.type: {
        const dismissed = new Set(
          draft.dismissedIdsByCompany[action.companyId] || []
        );
        const filtered = action.notifications.filter(
          (n) => !dismissed.has(n.id)
        );
        notificationsAdapter.updateMany(filtered, draft.notifications);
        draft.count = action.count;
        return draft;
      }
      case getUnreadNotificationsSuccessAction.type: {
        const dismissed = new Set(
          draft.dismissedIdsByCompany[action.companyId] || []
        );
        const filtered = action.notifications.filter(
          (n) => !dismissed.has(n.id)
        );
        unreadNotificationsAdapter.addMany(
          filtered,
          draft.unreadNotifications
        );
        draft.unreadCount = filtered.length;
        return draft;
      }
      case putReadNotificationsSuccessAction.type:
        unreadNotificationsAdapter.removeAll(draft.unreadNotifications);
        draft.unreadCount = 0;
        return draft;
      case putReadNotificationSuccessAction.type:
        unreadNotificationsAdapter.removeOne(
          action.id,
          draft.unreadNotifications
        );
        draft.unreadCount -= 1;
        return draft;
      case deleteNotificationSuccessAction.type: {
        const { id } = action;
        const wasInUnread = draft.unreadNotifications.ids.includes(id);
        notificationsAdapter.removeOne(id, draft.notifications);
        unreadNotificationsAdapter.removeOne(id, draft.unreadNotifications);
        if (wasInUnread) {
          draft.unreadCount = Math.max(0, draft.unreadCount - 1);
        }
        draft.count = Math.max(0, draft.count - 1);
        return draft;
      }
      case removeNotificationLocallySuccessAction.type: {
        const { companyId, id } = action;
        const wasInUnread = draft.unreadNotifications.ids.includes(id);
        notificationsAdapter.removeOne(id, draft.notifications);
        unreadNotificationsAdapter.removeOne(id, draft.unreadNotifications);
        if (wasInUnread) {
          draft.unreadCount = Math.max(0, draft.unreadCount - 1);
        }
        draft.count = Math.max(0, draft.count - 1);
        const prev = draft.dismissedIdsByCompany[companyId] || [];
        if (!prev.includes(id)) {
          const next = [...prev, id];
          draft.dismissedIdsByCompany[companyId] = next;
          saveDismissedNotificationIds(companyId, next);
        }
        return draft;
      }
      case hydrateDismissedNotificationsAction.type: {
        draft.dismissedIdsByCompany[action.companyId] =
          loadDismissedNotificationIds(action.companyId);
        return draft;
      }
      case getSystemNotificationsSuccessAction.type:
        draft.infoMessage = action.infoMessage;
        draft.infoMessageEs = action.infoMessageEs;
        draft.infoMessageEn = action.infoMessageEn;
        draft.infoMessagePt = action.infoMessagePt;
        return draft;
      case resetAction.type:
        return initialState;
      case getNotificationsInitAction.type:
      case getNotificationsFailureAction.type:
      case putReadNotificationsInitAction.type:
      case putReadNotificationsFailureAction.type:
      case putReadNotificationInitAction.type:
      case putReadNotificationFailureAction.type:
      case getSystemNotificationsInitAction.type:
      case getSystemNotificationsFailureAction.type:
      default:
        return draft;
    }
  }
);
